/**
 * multi-participant.js — meerdere kinderen tegelijk inschrijven.
 *
 * Werkt samen met snippets/participant-fields.liquid (blok-markup) en
 * assets/customer-prefill.js (profiel-prefill per blok).
 *
 * Gedrag:
 *  - "Nog een kind inschrijven" kloont het <template data-participant-template>
 *    en hangt het onder de bestaande blokken. Radio-namen krijgen per blok een
 *    ~N-suffix zodat radiogroepen per blok onafhankelijk blijven; het eerste
 *    blok houdt de kale namen zodat de native submit blijft werken.
 *  - De blokkeuze (variant) staat in elk deelnemer-blok: elk kind kan een
 *    ander blok krijgen.
 *  - Met één blok blijft de native Shopify form-submit ongewijzigd.
 *  - Met meerdere blokken wordt de submit onderschept en gaat elk kind als
 *    eigen line item (quantity 1) mee via POST /cart/add.js — aantallen en
 *    voorraad (bv. MP-blokken, DENY policy) blijven zo kloppen.
 */
(function () {
  'use strict';

  var PROP_RE = /^properties\[(.+?)\](~\d+)?$/;
  // Blok-suffix op een veldnaam: ~1, ~2, ... (~t1 is het tijdelijke suffix
  // dat addBlock gebruikt tot renumber() het definitieve nummer zet).
  var SUFFIX_RE = /~t?\d+$/;
  var uid = 1;

  var FIELD_REQUIRED_MSG = 'Dit veld is verplicht.';
  var fieldErrorUid = 1;

  // initAddonZoom() kan meerdere keren draaien (bv. na een section-herlaad in
  // de theme editor). Deze twee bewaken dat de gedelegeerde document-listener
  // maar één keer wordt geregistreerd en dat de sluitlogica per dialog-node
  // maar één keer wordt gebonden.
  var zoomDelegateBound = false;
  var zoomOpener = null;

  // initAddonSteppers() registreert gedelegeerde click/change-listeners op
  // document. init() draait opnieuw bij shopify:section:load, dus zonder
  // deze vlag zou elke herlaad een extra listener stapelen en een klik op
  // +/- meerdere keren tellen.
  var steppersBound = false;

  /**
   * Kiest een Nederlandse foutmelding op basis van de ValidityState van het
   * veld, zodat een wel-ingevuld maar ongeldig veld niet "verplicht" meldt.
   * Radiogroepen (alleen valueMissing mogelijk) gaan hier niet doorheen, zie
   * de aanroep in showFieldError.
   */
  function validationMessage(field) {
    var validity = field.validity;
    if (!validity) return FIELD_REQUIRED_MSG;
    if (validity.valueMissing) return FIELD_REQUIRED_MSG;
    if (validity.typeMismatch) {
      if (field.type === 'email') return 'Dit is geen geldig e-mailadres.';
      return 'Dit is geen geldige waarde.';
    }
    if (field.type === 'date' && (validity.badInput || validity.typeMismatch)) {
      return 'Dit is geen geldige datum.';
    }
    if (validity.badInput) return 'Dit is geen geldige waarde.';
    if (validity.rangeUnderflow || validity.rangeOverflow) {
      return 'Deze waarde valt buiten het toegestane bereik.';
    }
    if (validity.tooShort) return 'Deze waarde is te kort.';
    if (validity.tooLong) return 'Deze waarde is te lang.';
    if (validity.patternMismatch) return 'Dit is geen geldige waarde.';
    return field.validationMessage || 'Dit veld is ongeldig.';
  }

  /**
   * Zoekt de omsluitende veld-wrapper (snippets/participant-fields.liquid:
   * elk label + input/select/radiogroep zit in een .<prefix>__field div) zodat
   * de foutmelding op de juiste plek verschijnt, ongeacht de section-prefix.
   */
  function fieldWrap(el) {
    var node = el;
    while (node && node !== document.body) {
      var match = Array.prototype.some.call(node.classList || [], function (c) {
        return /__field$/.test(c);
      });
      if (match) return node;
      node = node.parentElement;
    }
    return el.parentElement;
  }

  /**
   * Zoekt binnen `wrap` het [data-field-error] dat bij `wrap` zelf hoort,
   * niet bij een geneste __field-wrapper eronder (bv. de shirtmaat-select
   * die met een eigen __field-div binnen de welkomscadeau-radiogroep zit,
   * zie snippets/participant-fields.liquid). Een gewone querySelector zou
   * het eerste [data-field-error] in document-volgorde pakken, ook als dat
   * van een genest veld is, waardoor twee velden hun foutmelding delen.
   */
  function ownFieldError(wrap) {
    if (!wrap) return null;
    var candidates = wrap.querySelectorAll('[data-field-error]');
    for (var i = 0; i < candidates.length; i++) {
      var candidate = candidates[i];
      if (fieldWrap(candidate.parentElement) === wrap) return candidate;
    }
    return null;
  }

  /**
   * Zet de zichtbare foutstaat: border-color op de veld(en), aria-invalid +
   * aria-describedby naar de foutmelding onder het veld. `ariaTargets` is
   * één input/select, of alle radio's + de groep-wrapper bij een radiogroep.
   */
  function showFieldError(field, ariaTargets) {
    var wrap = fieldWrap(field);
    var errorEl = ownFieldError(wrap);
    if (!errorEl) return;
    if (!errorEl.id) errorEl.id = 'field-error-' + fieldErrorUid++;
    errorEl.textContent = field.type === 'radio' ? FIELD_REQUIRED_MSG : validationMessage(field);
    errorEl.hidden = false;
    ariaTargets.forEach(function (el) {
      el.classList.add('has-error');
      el.setAttribute('aria-invalid', 'true');
      el.setAttribute('aria-describedby', errorEl.id);
    });
  }

  function clearFieldError(field, ariaTargets) {
    var wrap = fieldWrap(field);
    var errorEl = ownFieldError(wrap);
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    ariaTargets.forEach(function (el) {
      el.classList.remove('has-error');
      el.removeAttribute('aria-invalid');
      el.removeAttribute('aria-describedby');
    });
  }

  /**
   * `root` beperkt de herinitialisatie tot één sectie: shopify:section:load
   * geeft de vervangen sectie mee als event.target. Zonder die scoping zou
   * een herlaad van één sectie ook setup() opnieuw draaien op formulieren in
   * andere, niet-vervangen secties op dezelfde pagina en zo hun listeners
   * verdubbelen (setup() bindt rechtstreeks op de form/list-nodes, niet
   * gedelegeerd). De vervangen sectie zelf bevat altijd verse DOM-nodes, dus
   * daar is dubbel binden door setup() niet aan de orde.
   */
  function init(root) {
    (root || document).querySelectorAll('[data-participant-list]').forEach(function (list) {
      var form = list.closest('form');
      if (form) setup(form, list);
    });
    initAddonSteppers();
    initAddonZoom();
  }

  /**
   * Kleding bijbestellen (snippets/kleding-addon.liquid): plus/min-stepper
   * per rij; maat kiezen zet het aantal automatisch op 1. De teller in de
   * dichtgeklapte kop toont hoeveel stuks er gekozen zijn.
   *
   * Gedelegeerd op document, dus idempotent gemaakt met steppersBound:
   * zonder die guard zou een herlaad (init() opnieuw) deze click/change-
   * listeners een tweede keer registreren en elke klik dubbel tellen.
   */
  function initAddonSteppers() {
    if (steppersBound) return;
    steppersBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-addon-minus], [data-addon-plus]');
      if (!btn) return;
      var item = btn.closest('[data-addon-item]');
      var qty = item && item.querySelector('[data-addon-qty]');
      if (!qty) return;
      var value = parseInt(qty.value, 10) || 0;
      value += btn.hasAttribute('data-addon-plus') ? 1 : -1;
      qty.value = Math.max(0, Math.min(10, value));
      refreshAddonCount(item);
    });

    document.addEventListener('change', function (e) {
      var select = e.target.closest('[data-addon-variant]');
      if (!select) return;
      var item = select.closest('[data-addon-item]');
      var qty = item && item.querySelector('[data-addon-qty]');
      if (!qty) return;
      if (select.value && (parseInt(qty.value, 10) || 0) === 0) qty.value = '1';
      if (!select.value) qty.value = '0';
      refreshAddonCount(item);
    });
  }

  /**
   * Vergrootweergave van de productfoto (snippets/kleding-addon.liquid):
   * één gedeelde <dialog> voor alle rijen, gevuld met de data-zoom-* van de
   * aangeklikte knop. Puur presentationeel: raakt select/aantal nooit aan
   * en de triggers/sluitknop zijn altijd type="button", dus dit kan het
   * inschrijfformulier nooit submitten.
   *
   * De gedelegeerde document-listener wordt maar één keer geregistreerd
   * (zoomDelegateBound) en zoekt de dialog telkens opnieuw op in plaats van
   * 'm vast te houden, en de sluitlogica van de dialog zelf wordt per node
   * maar één keer gebonden (dataset.zoomBound). Zo stapelen de listeners
   * niet als initAddonZoom() nogmaals draait nadat de sectie is vervangen,
   * bijvoorbeeld in de theme editor.
   */
  function initAddonZoom() {
    if (!document.querySelector('[data-addon-zoom-dialog]')) return;
    if (!zoomDelegateBound) {
      zoomDelegateBound = true;
      document.addEventListener('click', handleAddonZoomTriggerClick);
    }
    bindAddonZoomDialog();
  }

  function handleAddonZoomTriggerClick(e) {
    var trigger = e.target.closest('[data-addon-zoom]');
    if (!trigger || trigger.disabled) return;
    var dialog = document.querySelector('[data-addon-zoom-dialog]');
    if (!dialog) return;
    var src = trigger.getAttribute('data-zoom-src');
    if (!src) return;
    var img = dialog.querySelector('[data-addon-zoom-img]');
    var caption = dialog.querySelector('[data-addon-zoom-caption]');
    var alt = trigger.getAttribute('data-zoom-alt') || '';
    if (img) {
      img.src = src;
      img.alt = alt;
    }
    if (caption) caption.textContent = alt;
    zoomOpener = trigger;
    // Bind close/backdrop listeners now: after a section reload this dialog
    // node is new and never went through initAddonZoom(). dataset.zoomBound
    // makes this a no-op when the dialog was already bound.
    bindAddonZoomDialog();
    dialog.showModal();
  }

  function bindAddonZoomDialog() {
    var dialog = document.querySelector('[data-addon-zoom-dialog]');
    if (!dialog || dialog.dataset.zoomBound) return;
    dialog.dataset.zoomBound = 'true';
    var closeBtn = dialog.querySelector('[data-addon-zoom-close]');

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        dialog.close();
      });
    }

    // Klik op de ::backdrop (buiten de kaart) sluit ook: die klik landt op
    // de dialog zelf, nooit op kleding-addon__zoom-inner.
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) dialog.close();
    });

    // Vangt zowel Escape (native 'cancel') als de sluitknop en de
    // backdrop-klik af: focus keert altijd terug naar de knop die opende.
    dialog.addEventListener('close', function () {
      var img = dialog.querySelector('[data-addon-zoom-img]');
      if (img) img.src = '';
      if (zoomOpener) zoomOpener.focus();
      zoomOpener = null; // release reference to a trigger button that may be removed from the DOM
    });
  }

  function refreshAddonCount(item) {
    var box = item.closest('[data-kleding-addon]');
    if (!box) return;
    var total = 0;
    box.querySelectorAll('[data-addon-qty]').forEach(function (q) {
      total += parseInt(q.value, 10) || 0;
    });
    var badge = box.querySelector('[data-addon-count]');
    if (badge) {
      badge.textContent = total + ' gekozen';
      badge.hidden = total === 0;
    }
  }

  /**
   * Verzamelt de gekozen kleding uit de addon-rijen in dezelfde section
   * als het formulier. Rijen met aantal > 0 zonder maat komen in `missing`.
   */
  function collectAddonItems(form) {
    var result = { items: [], missing: [] };
    var sectionEl = form.closest('[data-inschrijf-section]');
    if (!sectionEl) return result;

    sectionEl.querySelectorAll('[data-addon-item]').forEach(function (item) {
      var qtyEl = item.querySelector('[data-addon-qty]');
      var variantEl = item.querySelector('[data-addon-variant]');
      var qty = qtyEl ? parseInt(qtyEl.value, 10) || 0 : 0;
      if (qty < 1) return;
      var variantId = variantEl ? parseInt(variantEl.value, 10) : NaN;
      if (!variantId) {
        result.missing.push(item.dataset.addonName || 'kleding');
        return;
      }
      result.items.push({ id: variantId, quantity: qty });
    });
    return result;
  }

  function setup(form, list) {
    var tpl = form.querySelector('[data-participant-template]');
    var addBtn = form.querySelector('[data-add-participant]');

    function blocks() {
      return Array.prototype.slice.call(list.querySelectorAll('[data-participant]'));
    }

    /**
     * Hernummert de radiogroepen: blok 1 houdt de kale namen (name="id",
     * name="properties[...]") zodat de native submit van één kind blijft
     * werken, elk volgend blok krijgt ~1, ~2, ... Wordt ook na verwijderen
     * gedraaid, zodat het overgebleven blok weer de kale namen krijgt.
     */
    function renumber() {
      var bs = blocks();
      bs.forEach(function (b, i) {
        var suffix = i === 0 ? '' : '~' + i;
        b.querySelectorAll('input[type="radio"]').forEach(function (radio) {
          var name = radio.name.replace(SUFFIX_RE, '') + suffix;
          if (radio.name === name) return;
          // Hernoemen verplaatst de radio naar een andere groep; de browser kan
          // daarbij het vinkje weghalen, dus zetten we het daarna terug.
          var wasChecked = radio.checked;
          radio.name = name;
          radio.checked = wasChecked;
        });
        refreshVariantValidity(b);
        refreshShirtmaatVisibility(b);
        var rm = b.querySelector('[data-remove-participant]');
        if (rm) rm.hidden = bs.length === 1;
      });
    }

    function addBlock() {
      if (!tpl) return null;
      uid++;
      var frag = tpl.content.cloneNode(true);
      var block = frag.querySelector('[data-participant]');
      if (!block) return null;
      // Tijdelijk uniek suffix zodat het nieuwe blok bij het inhangen nooit in
      // de radiogroep van een bestaand blok valt; renumber() zet het definitief.
      block.querySelectorAll('input[type="radio"]').forEach(function (r) {
        r.name = r.name.replace(SUFFIX_RE, '') + '~t' + uid;
      });
      list.appendChild(frag);
      renumber();
      return block;
    }

    function removeBlock(block) {
      if (blocks().length <= 1) return;
      var idx = block.dataset.childIdx;
      block.remove();
      renumber();
      // Was dit blok aan een kindprofiel-checkbox gekoppeld? Dan uitvinken.
      if (idx) {
        var checkbox = form.querySelector('[data-prefill-child="' + idx + '"]');
        if (checkbox && checkbox.checked) {
          checkbox.checked = false;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // API voor customer-prefill.js (aanvinken van kindprofielen).
    form.__participants = {
      add: addBlock,
      remove: removeBlock,
      count: function () { return blocks().length; },
      // customer-prefill.js zet/wist de "Reeds deelnemer"-radio programmatisch
      // (geen change-event), dus roept dit expliciet aan om de shirtmaat-reveal
      // in sync te houden.
      syncShirtmaat: refreshShirtmaatVisibility
    };

    if (addBtn && tpl) {
      addBtn.addEventListener('click', function () {
        var block = addBlock();
        if (!block) return;
        block.scrollIntoView({ behavior: 'smooth', block: 'start' });
        var first = block.querySelector('input, select');
        if (first) first.focus({ preventScroll: true });
      });
    }

    list.addEventListener('change', function (e) {
      var radio = e.target.closest('[data-variant-radio]');
      if (!radio) return;
      var block = radio.closest('[data-participant]');
      if (block) refreshVariantValidity(block);
    });

    // "Gratis welkomscadeau": maatkeuze alleen tonen + verplichten bij de
    // tweede optie (data-welkomscadeau-nieuw).
    list.addEventListener('change', function (e) {
      var radio = e.target.closest('[data-welkomscadeau-radio]');
      if (!radio) return;
      var block = radio.closest('[data-participant]');
      if (block) refreshShirtmaatVisibility(block);
    });

    list.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-remove-participant]');
      if (!rm) return;
      var block = rm.closest('[data-participant]');
      if (block) removeBlock(block);
    });

    // Foutmelding verdwijnt zodra het veld gevuld wordt (tekst/select/datum
    // via 'input', radio's via 'change'). Voor een radiogroep telt elke klik
    // in de groep, niet alleen op de radio die het required-attribuut draagt
    // (de browser splitst de required-constraint over de hele groep).
    ['input', 'change'].forEach(function (evt) {
      list.addEventListener(evt, function (e) {
        var field = e.target;
        if (!field.matches || !field.matches('input, select, textarea')) return;
        if (field.hasAttribute('data-variant-radio')) return;
        var block = field.closest('[data-participant]');
        if (!block) return;

        if (field.type === 'radio') {
          if (!field.checked) return;
          var radios = Array.prototype.slice.call(
            block.querySelectorAll('input[name="' + field.name + '"]')
          );
          var groupEl = field.closest('[class*="__radio-group"]') || field.parentElement;
          clearFieldError(field, radios.concat(groupEl));
          return;
        }

        if (field.hasAttribute('required') && field.value.trim() !== '') {
          clearFieldError(field, [field]);
        }
      });
    });

    /**
     * Vervangt de standaard browsermelding door een zichtbare Nederlandse
     * foutmelding bij een leeg verplicht deelnemerveld. 'invalid' bubbelt
     * niet, dus we luisteren in de capture-fase op `list`; preventDefault()
     * onderdrukt alleen de native bubble (het formulier blijft ongeldig en
     * submit blijft geblokkeerd). Bij een radiogroep vuurt de browser
     * 'invalid' op elke radio in de groep, niet alleen op degene met het
     * required-attribuut, dus die check ontbreekt hier bewust. De blokkeuze
     * (data-variant-radio) heeft al eigen Nederlandse afhandeling
     * (missingBlock/setCustomValidity hieronder) en velden buiten het
     * deelnemerblok (ouder/toestemming) houden hun native melding.
     *
     * De browser vuurt 'invalid' synchroon voor elk ongeldig veld in
     * documentvolgorde (één aaneengesloten reeks per verzendpoging), en
     * focust daarna zelf nog de eerste NIET-onderdrukte invalid control in
     * het hele formulier (bv. een leeg ouderveld verderop), dat overschrijft
     * een focus() die we tijdens de reeks zelf al aanroepen. Daarom stellen
     * we onze focus uit tot de microtask-queue, na die native stap, zodat de
     * eerste foutieve deelnemerveld het laatste woord houdt.
     */
    var focusedThisAttempt = false;
    list.addEventListener('invalid', function (e) {
      var field = e.target;
      if (field.hasAttribute('data-variant-radio')) return;
      e.preventDefault();

      var block = field.closest('[data-participant]');
      if (!block) return;

      if (field.type === 'radio') {
        var radios = Array.prototype.slice.call(
          block.querySelectorAll('input[name="' + field.name + '"]')
        );
        var groupEl = field.closest('[class*="__radio-group"]') || field.parentElement;
        showFieldError(field, radios.concat(groupEl));
      } else {
        showFieldError(field, [field]);
      }

      if (!focusedThisAttempt) {
        focusedThisAttempt = true;
        Promise.resolve().then(function () {
          field.focus();
          field.scrollIntoView({ behavior: 'smooth', block: 'center' });
          focusedThisAttempt = false;
        });
      }
    }, true);

    form.addEventListener('submit', function (e) {
      var bs = blocks();
      var addon = collectAddonItems(form);
      var errorBox = form.querySelector('[data-cart-error]');
      var submitBtn = form.querySelector('button[type="submit"]');

      // Elk kind kiest zijn eigen blok. Producten zonder blokken (alleen een
      // default variant) hebben in plaats daarvan één vaste variant in het form.
      var fixedVariant = form.querySelector('input[name="id"][type="hidden"]');
      var missingBlock = [];
      var variantIds = bs.map(function (b, i) {
        var picked = b.querySelector('[data-variant-radio]:checked');
        if (picked) return parseInt(picked.value, 10);
        if (b.querySelector('[data-variant-radio]')) {
          missingBlock.push(childLabel(b, i));
          return NaN;
        }
        return fixedVariant ? parseInt(fixedVariant.value, 10) : NaN;
      });

      // Deze controle draait ook op de native flow: zonder blok geen inschrijving.
      if (missingBlock.length) {
        e.preventDefault();
        showError(errorBox, 'Kies een blok voor: ' + missingBlock.join(', ') + '.');
        return;
      }
      if (variantIds.some(function (id) { return !id; })) {
        e.preventDefault();
        showError(errorBox, 'Kies eerst een blok.');
        return;
      }

      // Native flow alleen bij één kind zonder kleding erbij
      if (bs.length < 2 && addon.items.length === 0 && addon.missing.length === 0) return;

      e.preventDefault();

      if (addon.missing.length) {
        showError(errorBox, 'Kies een maat voor: ' + addon.missing.join(', ') + '.');
        return;
      }

      // Gedeelde properties: alles buiten de deelnemer-blokken (ouder + overig)
      var shared = collectProps(form, function (el) {
        return !el.closest('[data-participant]');
      });

      var items = bs.map(function (b, i) {
        var props = Object.assign({}, shared, collectProps(b, function () { return true; }));
        return { id: variantIds[i], quantity: 1, properties: props };
      });

      // Kleding als eigen line items in dezelfde order
      items = items.concat(addon.items);

      if (errorBox) errorBox.hidden = true;
      var btnText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Bezig met inschrijven...';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (res.ok) {
            window.location.href = '/cart';
            return;
          }
          return res.json().then(function (data) {
            var msg = (data && (data.description || data.message)) || 'Inschrijven is niet gelukt.';
            throw new Error(msg);
          });
        })
        .catch(function (err) {
          showError(errorBox, err.message || 'Inschrijven is niet gelukt. Probeer het opnieuw.');
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = btnText;
          }
        });
    });

    renumber();
  }

  /**
   * Zet de native validatiemelding van de blokkeuze in het Nederlands, zodat
   * een kind zonder blok niet verzonden kan worden. Leeg zodra er een blok
   * gekozen is, anders blijft de radiogroep ongeldig.
   */
  function refreshVariantValidity(block) {
    var radios = block.querySelectorAll('[data-variant-radio]');
    if (!radios.length) return;
    var message = block.querySelector('[data-variant-radio]:checked') ? '' : 'Kies een blok voor dit kind.';
    radios.forEach(function (radio) {
      radio.setCustomValidity(message);
    });
  }

  /**
   * "Gratis welkomscadeau": de shirtmaat staat in de HTML zichtbaar +
   * required (zo blijft het formulier zonder JS bruikbaar). Het veld leeft
   * in dezelfde kaart als optie 2 (nieuwe deelnemer/shirtmaat) en klapt
   * dicht via de .is-collapsed class (CSS animeert opacity/transform/hoogte
   * met een grid-template-rows-truc, i.p.v. het [hidden]-attribuut, zodat de
   * reveal soepel kan). Alleen bij de eerste optie (al deelnemer, geen
   * data-welkomscadeau-nieuw) klappen en disablen we 'm hier: een disabled
   * select wordt door de browser niet gevalideerd en niet meegestuurd, dus
   * dan gaat er geen (lege) properties[Shirtmaat] mee. Bij de tweede optie,
   * of nog geen keuze, blijft de maat zichtbaar en verplicht.
   */
  function refreshShirtmaatVisibility(block) {
    var field = block.querySelector('[data-shirtmaat-field]');
    var select = block.querySelector('[data-shirtmaat-select]');
    if (!field || !select) return;
    var alreadyParticipant = !!block.querySelector('[data-welkomscadeau-radio]:checked:not([data-welkomscadeau-nieuw])');
    field.classList.toggle('is-collapsed', alreadyParticipant);
    field.setAttribute('aria-hidden', alreadyParticipant ? 'true' : 'false');
    select.disabled = alreadyParticipant;
    if (alreadyParticipant) {
      select.value = '';
      clearFieldError(select, [select]);
    }
  }

  // Naam van het kind voor foutmeldingen, met "kind N" als terugval.
  function childLabel(block, index) {
    var input = block.querySelector('[name^="properties[Voornaam]"]');
    var name = input ? input.value.trim() : '';
    return name || 'kind ' + (index + 1);
  }

  function collectProps(root, include) {
    var out = {};
    root
      .querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]')
      .forEach(function (el) {
        if (!include(el)) return;
        var m = PROP_RE.exec(el.name);
        if (!m) return;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        if (el.value === '') return;
        out[m[1]] = el.value;
      });
    return out;
  }

  function showError(box, msg) {
    if (!box) {
      window.alert(msg);
      return;
    }
    box.textContent = msg;
    box.hidden = false;
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Herinitialiseert alleen de zojuist herladen sectie (theme editor). Dit
  // script laadt eenmalig via layout/theme.liquid en draait niet opnieuw bij
  // een section-herlaad, dus deze registratie zelf kan niet dubbel lopen.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
